/**
 * Build the Reports page Excel workbook off the data the page already has.
 *
 * Trend samples from different devices rarely share a timestamp. The old export
 * made one row per raw timestamp and, for every cell, scanned that series for a
 * sample within 60 seconds. That is O(timestamps × series × points). When clocks
 * differ, the timestamp set is about series × points, so a building-sized export
 * (hundreds of series, a day or two of minute data) locks the browser tab until
 * it runs out of memory.
 *
 * Rows are now one per 60-second bucket. Each sample lands in the bucket nearest
 * its timestamp (always within 30 seconds), and a bucket keeps the sample closest
 * to the label. Aligned minute data keeps one row per sample. The sheet stays
 * proportional to the time range, which SheetJS can write for the sizes this
 * page is asked to export.
 */

const XLSX = require('xlsx');

const TREND_BUCKET_MS = 60 * 1000;

/**
 * Above this many cells, SheetJS needs on the order of a gigabyte and writes a
 * file large enough to get the tab killed. Fail before allocating that sheet.
 * 281 series × about 7 days of minute buckets sits just under the limit.
 */
const MAX_TREND_CELLS = 3000000;

/**
 * @description Round to two decimals. Blank when the sample is missing.
 * @param {number|null|undefined} num
 * @returns {number|string}
 */
function round100(num) {
    if (num == null || num === '' || Number.isNaN(num)) return '';
    return Math.round(num * 100) / 100;
}

/**
 * @description Zone label used in both sheets. A site-level point has no zone name.
 * @param {{ measurementZone?: string }} row
 * @returns {string}
 */
function deviceName(row) {
    return (row.measurementZone && row.measurementZone !== 'site') ? row.measurementZone : '(Site)';
}

/**
 * @description Format a timestamp the same way the Reports page trend sheet always has.
 * @param {number} ts
 * @param {string} timeZone
 * @returns {string}
 */
function formatTimestamp(ts, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
        timeZoneName: 'longOffset'
    }).formatToParts(new Date(ts));
    const get = (type) => parts.find((part) => part.type === type).value;
    let offset = get('timeZoneName').replace('GMT', '');
    if (offset === '' || offset === 'Z') offset = '+00:00';
    const match = offset.match(/^([+-])(\d{1,2})(?::(\d{2}))?$/);
    if (match) offset = `${match[1]}${match[2].padStart(2, '0')}:${match[3] || '00'}`;
    return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')} ${offset}`;
}

/**
 * @description Summary sheet rows. Column layout matches the on-screen report table.
 * @param {object[]} reportData
 * @param {{ title: string, periodLabel: string, showScore: boolean }} options
 * @returns {Array<Array<string|number>>}
 */
function buildSummaryRows(reportData, options) {
    const rows = Array.isArray(reportData) ? reportData : [];
    const showScore = options.showScore === true;
    const hasBool = rows.some((row) => row.measurementKind === 'Bool');
    const hasNumeric = rows.some((row) => row.measurementKind !== 'Bool');
    let headers = ['Device', 'Measurement', 'Time Range'];
    if (hasNumeric) {
        headers = headers.concat(['Min Value', 'Max Value', 'Average Value']);
        if (showScore) headers.push('Min Score', 'Max Score', 'Average Score');
    }
    if (hasBool) headers.push('Count');

    const summaryRows = [[options.title], [options.periodLabel], [], headers];
    for (const row of rows) {
        const name = deviceName(row);
        const intervals = row.intervals || [];
        for (let i = 0; i < intervals.length; i++) {
            const interval = intervals[i];
            const out = [name, row.measurementName, interval.intervalName];
            if (row.measurementKind === 'Bool') {
                if (hasNumeric) out.push('', '', '');
                if (showScore && hasNumeric) out.push('', '', '');
                out.push(interval.count);
            } else {
                if (hasNumeric) {
                    out.push(round100(interval.minVal), round100(interval.maxVal), round100(interval.aveVal));
                    if (showScore) out.push(round100(interval.minScore), round100(interval.maxScore), round100(interval.aveScore));
                }
                if (hasBool) out.push('');
            }
            summaryRows.push(out);
        }
    }
    return summaryRows;
}

/**
 * @description Accept either `[ts, value]` pairs or a transferred Float64Array of pairs.
 * @param {Array<[number, *]>|Float64Array} samples
 * @returns {Float64Array}
 */
function asInterleaved(samples) {
    if (samples instanceof Float64Array) return samples;
    const out = new Float64Array(samples.length * 2);
    for (let i = 0; i < samples.length; i++) {
        out[i * 2] = samples[i][0];
        const value = samples[i][1];
        out[i * 2 + 1] = typeof value === 'number' ? value : Number.NaN;
    }
    return out;
}

/**
 * @description Trend sheet rows: header plus one row per occupied 60-second bucket.
 * @param {{ name: string, samples: Array<[number, *]>|Float64Array }[]} series
 * @param {{ timeZone: string, startTime: number, endTime: number, bucketMs?: number }} options
 * @returns {Array<Array<string|number>>}
 */
function buildTrendRows(series, options) {
    const bucketMs = options.bucketMs || TREND_BUCKET_MS;
    const packed = [];
    for (const entry of series || []) {
        if (!entry || !entry.name) continue;
        const samples = entry.samples && entry.samples.length > 0 ? asInterleaved(entry.samples) : new Float64Array(0);
        packed.push({ name: entry.name, samples });
    }
    if (packed.length === 0) return [];

    let min = Infinity;
    let max = -Infinity;
    for (let s = 0; s < packed.length; s++) {
        const samples = packed[s].samples;
        for (let i = 0; i < samples.length; i += 2) {
            const ts = samples[i];
            const value = samples[i + 1];
            if (Number.isNaN(value) || ts < options.startTime || ts > options.endTime) continue;
            const bucket = Math.round(ts / bucketMs) * bucketMs;
            if (bucket < min) min = bucket;
            if (bucket > max) max = bucket;
        }
    }
    if (!Number.isFinite(min)) {
        return [['Timestamp'].concat(packed.map((entry) => entry.name))];
    }

    const bucketCount = Math.round((max - min) / bucketMs) + 1;
    const cells = bucketCount * (packed.length + 1);
    if (cells > MAX_TREND_CELLS) {
        const error = new Error(`Trend sheet is too large (${bucketCount.toLocaleString()} rows × ${packed.length.toLocaleString()} measurements). Narrow the date range or the selection and try again.`);
        error.code = 'TREND_EXPORT_TOO_LARGE';
        throw error;
    }

    const bestDiff = packed.map(() => new Float64Array(bucketCount));
    const bestTs = packed.map(() => new Float64Array(bucketCount));
    const bestVal = packed.map(() => new Float64Array(bucketCount));
    for (let s = 0; s < packed.length; s++) bestDiff[s].fill(Infinity);
    const occupied = new Uint8Array(bucketCount);

    for (let s = 0; s < packed.length; s++) {
        const samples = packed[s].samples;
        const diffs = bestDiff[s];
        const stamps = bestTs[s];
        const values = bestVal[s];
        for (let i = 0; i < samples.length; i += 2) {
            const ts = samples[i];
            const value = samples[i + 1];
            if (Number.isNaN(value) || ts < options.startTime || ts > options.endTime) continue;
            const bucket = Math.round(ts / bucketMs) * bucketMs;
            const index = Math.round((bucket - min) / bucketMs);
            const diff = Math.abs(ts - bucket);
            if (diff < diffs[index] || (diff === diffs[index] && ts < stamps[index])) {
                diffs[index] = diff;
                stamps[index] = ts;
                values[index] = value;
                occupied[index] = 1;
            }
        }
    }

    const trendRows = [['Timestamp'].concat(packed.map((entry) => entry.name))];
    for (let index = 0; index < bucketCount; index++) {
        if (!occupied[index]) continue;
        const row = new Array(packed.length + 1);
        row[0] = formatTimestamp(min + index * bucketMs, options.timeZone);
        for (let s = 0; s < packed.length; s++) {
            row[s + 1] = bestDiff[s][index] === Infinity ? '' : round100(bestVal[s][index]);
        }
        trendRows.push(row);
    }
    return trendRows;
}

/**
 * @description Write the summary sheet and, when present, the trend sheet.
 * @param {{ summaryRows: Array<Array<string|number>>, trendRows?: Array<Array<string|number>> }} sheets
 * @returns {Uint8Array}
 */
function buildWorkbookBuffer(sheets) {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(sheets.summaryRows), 'Summary');
    if (sheets.trendRows && sheets.trendRows.length > 0) {
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(sheets.trendRows), 'Trend Data');
    }
    const bytes = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
    return bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
}

module.exports = {
    TREND_BUCKET_MS,
    MAX_TREND_CELLS,
    round100,
    formatTimestamp,
    buildSummaryRows,
    buildTrendRows,
    buildWorkbookBuffer
};
